import type { SuggestionRoute } from "@/hooks/useLiveSuggestions";
import { RESEARCH_QUESTION_SENTENCES, SEED_TARGET_LABEL } from "@/lib/conceptGuidance";

export type RecStatus = "recommended" | "available" | "blocked";

export interface ChecklistItem {
  label: string;
  done: boolean;
}

export interface WorkflowAction {
  to: SuggestionRoute;
  search?: Record<string, unknown>;
}

export interface WorkflowRecommendation {
  id: string;
  status: RecStatus;
  title: string;
  rationale: string; // "why now" — must be non-empty
  unlocks: string;
  checklist: ChecklistItem[];
  action: WorkflowAction;
  walkthroughId?: string;
  /** Section anchor within `walkthroughId`'s MDX to deep-link/scroll to. */
  walkthroughSection?: string;
  priority: number;
}

export interface ConceptSnapshot {
  hasDescription: boolean;
  /** Research question was auto-drafted on create and not yet reviewed. */
  draftedQuestion: boolean;
  seedCount: number;
  foundCount: number;
  unscoredCount: number;
  scoredCount: number;
  papersOutOfBoundary: number;
  conceptEdited: boolean;
  atScoringLimit: boolean;
  hasTopPaper: boolean;
  topPaperOpened: boolean;
  reviewedCount: number;
  hasFieldDebate: boolean;
  campSurfaced: boolean;
  auditEnacted: boolean;
  drillEnacted: boolean;
  hasChatMessages: boolean;
  hasHighlights: boolean;
  hasClaims: boolean;
  hasClaimsWithEvidence: boolean;
  hasSmartTrawlPapers: boolean;
  hasArgumentAnalysis: boolean;
  hasDraft: boolean;
  untriagedCount: number;
  isPaid: boolean;
  /** Field Debate corpus has drifted since generation. Only the Analyze
   *  surfaces know this (it needs the analytics papers query) — they pass it
   *  via useAnalyzeRecs; everywhere else it's absent (= not stale). */
  debateStale?: boolean;
}

export const EMPTY_SNAPSHOT: ConceptSnapshot = {
  hasDescription: false,
  draftedQuestion: false,
  seedCount: 0,
  foundCount: 0,
  unscoredCount: 0,
  scoredCount: 0,
  papersOutOfBoundary: 0,
  conceptEdited: false,
  atScoringLimit: false,
  hasTopPaper: false,
  topPaperOpened: false,
  reviewedCount: 0,
  hasFieldDebate: false,
  campSurfaced: false,
  auditEnacted: false,
  drillEnacted: false,
  hasChatMessages: false,
  hasHighlights: false,
  hasClaims: false,
  hasClaimsWithEvidence: false,
  hasSmartTrawlPapers: false,
  hasArgumentAnalysis: false,
  hasDraft: false,
  untriagedCount: 0,
  isPaid: false,
};

const CURATE: SuggestionRoute = "/concepts/$conceptId/curate";
const ANALYZE: SuggestionRoute = "/concepts/$conceptId/analyze";
const BUILD: SuggestionRoute = "/concepts/$conceptId/build";
const OUTLINE: SuggestionRoute = "/concepts/$conceptId/import/outline";

type RuleKind = "primary" | "branch" | "gate";

interface RuleDef {
  id: string;
  priority: number;
  kind: RuleKind;
  live: (s: ConceptSnapshot) => boolean;
  build: (s: ConceptSnapshot) => Omit<WorkflowRecommendation, "status" | "priority" | "id">;
}

const RULES: readonly RuleDef[] = [
  {
    id: "review_drafted_question",
    priority: 5,
    kind: "primary",
    live: (s) => s.draftedQuestion,
    build: () => ({
      title: "Review your drafted research question",
      rationale: "PaperChase drafted your research question from the concept name. Every paper is graded against it, so check it says what you mean.",
      unlocks: "Scores you can trust.",
      checklist: [{ label: "Edit the research question or confirm it looks right", done: false }],
      action: { to: CURATE },
      walkthroughId: "conceptual-frameworks",
    }),
  },
  {
    id: "frame_concept",
    priority: 10,
    kind: "primary",
    live: (s) => !s.hasDescription,
    build: () => ({
      title: "Add a research question",
      rationale: "Seed discovery and scoring both read your research question.",
      unlocks: "Seed discovery and automatic scoring.",
      checklist: [{ label: `Write a ${RESEARCH_QUESTION_SENTENCES} sentence research question`, done: false }],
      action: { to: CURATE },
      walkthroughId: "conceptual-frameworks",
    }),
  },
  {
    id: "add_seeds",
    priority: 15,
    kind: "primary",
    live: (s) => s.hasDescription && s.seedCount === 0 && s.foundCount === 0,
    build: () => ({
      title: "Add seed papers",
      rationale: "Seeds are your starting papers — PaperChase follows their citations to find the rest of your corpus.",
      unlocks: "Trawling and a scored corpus.",
      checklist: [{ label: `Add ${SEED_TARGET_LABEL} seed papers`, done: false }],
      action: { to: CURATE },
      walkthroughId: "discovery-via-citation-networks",
    }),
  },
  {
    id: "trawl",
    priority: 20,
    kind: "primary",
    live: (s) => s.hasDescription && s.seedCount > 0 && s.foundCount === 0,
    build: () => ({
      title: "Trawl from your seeds",
      rationale: "Your seed papers are set — trawling expands them into a corpus and scores it automatically.",
      unlocks: "A scored corpus to review.",
      checklist: [{ label: "Run a trawl from seed papers", done: false }],
      action: { to: CURATE },
      walkthroughId: "discovery-via-citation-networks",
    }),
  },
  {
    id: "score_gate",
    priority: 25,
    kind: "gate",
    live: (s) => s.unscoredCount > 0 && s.atScoringLimit,
    build: (s) => ({
      title: "Upgrade to score the rest",
      rationale: `You've hit your scoring limit with ${s.unscoredCount} paper(s) still unscored.`,
      unlocks: "Scoring for your remaining papers.",
      checklist: [{ label: "Upgrade your plan", done: false }],
      action: { to: CURATE },
      walkthroughId: "corpus-curation",
    }),
  },
  {
    id: "score_imports",
    priority: 30,
    kind: "primary",
    live: (s) => s.unscoredCount > 0 && !s.atScoringLimit,
    build: (s) => ({
      title: `Score ${s.unscoredCount} unscored papers`,
      rationale: "These papers aren't scored yet — score them to rank them against your concept.",
      unlocks: "A complete ranking.",
      checklist: [{ label: `Score ${s.unscoredCount} unscored paper(s)`, done: false }],
      action: { to: CURATE },
      walkthroughId: "corpus-curation",
    }),
  },
  {
    id: "rescore",
    priority: 35,
    kind: "primary",
    live: (s) => s.conceptEdited && s.papersOutOfBoundary > 0,
    build: (s) => ({
      title: `Re-score ${s.papersOutOfBoundary} papers`,
      rationale: "You edited the concept, so some scores are now stale.",
      unlocks: "Scores that match your updated concept.",
      checklist: [{ label: `Re-score ${s.papersOutOfBoundary} stale paper(s)`, done: false }],
      action: { to: CURATE },
      walkthroughId: "paperchase-learns-from-you",
    }),
  },
  {
    id: "review",
    priority: 40,
    kind: "primary",
    live: (s) => s.scoredCount > 0 && s.reviewedCount === 0 && s.hasTopPaper,
    build: () => ({
      title: "Review your top papers",
      rationale: "Your corpus is scored — reading the top few validates whether the ranking is well-calibrated.",
      unlocks: "Confidence in your scores, and the analysis tools.",
      checklist: [
        { label: "Open the top-ranked paper", done: false },
        { label: "Rate, note or read a few of them", done: false },
      ],
      action: { to: CURATE },
      walkthroughId: "corpus-curation",
    }),
  },
  {
    id: "field_debate",
    priority: 50,
    kind: "primary",
    live: (s) => s.scoredCount >= 15 && !s.hasFieldDebate,
    build: () => ({
      title: "Run Field Debate",
      rationale: "You have enough scored literature to map the field's camps and tensions.",
      unlocks: "Coverage audit, camp drill-down, and chat.",
      checklist: [{ label: "Generate the Field Debate map", done: false }],
      // autorun so the "Run Field Debate" CTA actually generates (the deep-link
      // effect runs it), not just opens an empty panel. drill (below) omits
      // autorun — it should only open the existing map.
      action: { to: ANALYZE, search: { activeTool: "debate", autorun: true } },
      walkthroughId: "leveraging-the-analyze-page",
    }),
  },
  {
    // Before audit: auditing a corpus the debate map no longer describes
    // gives inconsistent guidance — bring the map current first (the retired
    // headline engine's rerun-debate precedence, now in the one engine).
    id: "refresh_debate",
    priority: 54,
    kind: "branch",
    live: (s) => s.hasFieldDebate && !!s.debateStale,
    build: () => ({
      title: "Refresh Field Debate",
      rationale: "Your corpus has shifted since the debate map was built.",
      unlocks: "Camps and tensions that match the papers you have now.",
      checklist: [{ label: "Refresh or rebuild the Field Debate map", done: false }],
      action: { to: ANALYZE, search: { activeTool: "debate" } },
      walkthroughId: "leveraging-the-analyze-page",
    }),
  },
  {
    id: "audit",
    priority: 55,
    kind: "branch",
    live: (s) => s.hasFieldDebate && !s.auditEnacted,
    build: () => ({
      title: "Audit coverage",
      rationale: "Field Debate is mapped — check where your corpus is thin.",
      unlocks: "Gap-fill trawl targets.",
      checklist: [{ label: "Run the coverage audit", done: false }],
      action: { to: ANALYZE, search: { activeTool: "audit", autorun: true } },
      walkthroughId: "leveraging-the-analyze-page",
    }),
  },
  {
    id: "drill",
    priority: 57,
    kind: "branch",
    live: (s) => s.campSurfaced && !s.drillEnacted,
    build: () => ({
      title: "Drill into a camp",
      rationale: "Field Debate surfaced camps — drill in to see who argues what.",
      unlocks: "A focused view of one position.",
      checklist: [{ label: "Open a camp in Field Debate", done: false }],
      action: { to: ANALYZE, search: { activeTool: "debate" } },
      walkthroughId: "leveraging-the-analyze-page",
    }),
  },
  {
    id: "chat",
    priority: 60,
    kind: "branch",
    live: (s) => s.scoredCount > 0 && !s.hasChatMessages,
    build: () => ({
      title: "Ask your corpus a question",
      rationale: "Your scored corpus can answer targeted questions in chat.",
      unlocks: "Fast answers grounded in your papers.",
      checklist: [{ label: "Ask a question in chat", done: false }],
      action: { to: ANALYZE, search: { activeTool: "chat" } },
      walkthroughId: "leveraging-the-analyze-page",
    }),
  },
  {
    id: "highlight",
    priority: 62,
    kind: "branch",
    live: (s) => s.scoredCount > 0 && !s.hasHighlights,
    build: () => ({
      title: "Highlight key passages",
      rationale: "As you read full-text PDFs, highlight passages worth citing — they become evidence for your claims.",
      unlocks: "Evidence you can attach to claims.",
      checklist: [{ label: "Open a paper's full text and highlight a passage", done: false }],
      action: { to: CURATE },
      walkthroughId: "building-your-argument",
    }),
  },
  {
    id: "refine",
    priority: 65,
    kind: "branch",
    live: (s) => s.scoredCount > 0,
    build: () => ({
      title: "Refine your concept",
      rationale: "If the results look off-target, tightening the concept improves every score.",
      unlocks: "Sharper scoring and trawling.",
      checklist: [{ label: "Edit the concept definition", done: false }],
      action: { to: CURATE },
      walkthroughId: "conceptual-frameworks",
    }),
  },
  {
    id: "snowball",
    priority: 68,
    kind: "branch",
    live: (s) => s.scoredCount > 0,
    build: () => ({
      title: "Snowball from your top papers",
      rationale: "Follow citations from your best-ranked papers to surface more of the field.",
      unlocks: "Broader coverage.",
      checklist: [{ label: "Run a snowball expansion", done: false }],
      action: { to: CURATE },
      walkthroughId: "discovery-via-citation-networks",
    }),
  },
  {
    id: "smart_trawl",
    priority: 70,
    kind: "branch",
    live: (s) => s.hasHighlights,
    build: () => ({
      title: "Spot Trawl from your highlights",
      rationale: "Your highlights look like trawl seeds — find more like them.",
      unlocks: "Targeted new papers.",
      checklist: [{ label: "Run Spot Trawl", done: false }],
      action: { to: CURATE },
      walkthroughId: "discovery-via-citation-networks",
    }),
  },
  {
    id: "outline_import",
    priority: 72,
    kind: "branch",
    live: (s) => s.isPaid && s.foundCount > 0 && !s.hasClaims,
    build: () => ({
      title: "Import a draft outline",
      rationale: "Have a draft already? Import its outline to seed your claims.",
      unlocks: "A claim scaffold.",
      checklist: [{ label: "Import an outline", done: false }],
      action: { to: OUTLINE },
      walkthroughId: "building-your-argument",
    }),
  },
  {
    id: "first_claim",
    priority: 74,
    kind: "branch",
    live: (s) => s.foundCount > 0 && !s.hasClaims,
    build: () => ({
      title: "Add your first claim",
      rationale: "Turn your reading into an argument by stating a claim.",
      unlocks: "Evidence attachment and critique.",
      checklist: [{ label: "Write a claim", done: false }],
      action: { to: BUILD, search: { view: "map" } },
      walkthroughId: "building-your-argument",
    }),
  },
  {
    id: "attach_evidence",
    priority: 76,
    kind: "branch",
    live: (s) => s.hasHighlights && !s.hasClaimsWithEvidence,
    build: () => ({
      title: "Attach evidence to your claims",
      rationale: "Your highlights can back up your claims as evidence.",
      unlocks: "A defensible argument.",
      checklist: [{ label: "Attach a highlight to a claim", done: false }],
      action: { to: BUILD, search: { view: "map" } },
      walkthroughId: "building-your-argument",
    }),
  },
  {
    id: "critique",
    priority: 78,
    kind: "branch",
    live: (s) => s.hasClaimsWithEvidence && s.isPaid && !s.hasArgumentAnalysis,
    build: () => ({
      title: "Critique your argument",
      rationale: "Your claims have evidence — stress-test the argument before writing.",
      unlocks: "Weak spots to address.",
      checklist: [{ label: "Run the argument critique", done: false }],
      action: { to: BUILD, search: { view: "map", critique: true } },
      walkthroughId: "building-your-argument",
    }),
  },
  {
    id: "write",
    priority: 80,
    kind: "branch",
    live: (s) => s.isPaid && s.hasClaims && !s.hasDraft,
    build: () => ({
      title: "Start writing",
      rationale: "Your argument is taking shape — start drafting from it.",
      unlocks: "An exportable draft.",
      checklist: [{ label: "Open the writing workspace", done: false }],
      action: { to: BUILD, search: { view: "write" } },
      walkthroughId: "using-the-output",
    }),
  },
] as const;

/** Distinct `walkthroughId`s referenced across all rules. The drift-guard test
 *  asserts each one resolves to a live guide walkthrough (see workflowAdvisor.test). */
export const RULE_WALKTHROUGH_IDS: string[] = Array.from(
  new Set(
    RULES.map((r) => r.build(EMPTY_SNAPSHOT).walkthroughId).filter(
      (id): id is string => !!id,
    ),
  ),
);

/** Every rule id, for drift-guard tests. */
export const RULE_IDS: string[] = RULES.map((r) => r.id);

/**
 * Per-rule target section anchor within the rule's walkthrough, so a guide
 * deep-link scrolls to the precise `<Section id>` instead of the page top.
 * Each value must match a `<Section id="...">` in the rule's walkthrough MDX;
 * a stale/missing id degrades gracefully (the overlay just opens at the top).
 */
export const RULE_SECTIONS: Record<string, string> = {
  review_drafted_question: "what-a-concept-is",
  frame_concept: "what-a-concept-is",
  add_seeds: "seeds-as-anchors",
  trawl: "trawl",
  score_gate: "scoring",
  score_imports: "scoring",
  rescore: "feedback-loop",
  review: "reading",
  field_debate: "field-debate",
  refresh_debate: "field-debate",
  audit: "coverage-audit",
  drill: "camp-drill",
  chat: "chat",
  highlight: "evidence",
  refine: "iterating",
  snowball: "snowball",
  smart_trawl: "smart-trawl",
  outline_import: "outline-import",
  first_claim: "claims",
  attach_evidence: "evidence",
  critique: "critique",
  write: "writing-workspace",
};

/** Routes that belong to each workflow phase. Build owns the outline-import
 *  route too (it's a build surface even though it lives on its own route). */
export const PHASE_ROUTES: Record<"curate" | "analyze" | "build", readonly SuggestionRoute[]> = {
  curate: [CURATE],
  analyze: [ANALYZE],
  build: [BUILD, OUTLINE],
};

/**
 * Scope a recommendation list to one phase for that phase's "Next step" card.
 * Returns the recs whose action targets this phase (recommended + alternatives,
 * order preserved). When no in-phase rec is live, fall back to the global
 * *recommended* (primary) rec — which exists only when the user isn't ready for
 * this phase yet, giving an honest "do X first" pointer. Once the phase's own
 * work is done there is no recommended rec, so the result is empty and the card
 * hides instead of surfacing an unrelated lateral suggestion. Pass
 * already-filtered recs (e.g. blocked removed) — this only narrows by route.
 */
export function recsForPhase(
  recs: WorkflowRecommendation[],
  phase: keyof typeof PHASE_ROUTES,
): WorkflowRecommendation[] {
  const routes = PHASE_ROUTES[phase];
  const inPhase = recs.filter((r) => routes.includes(r.action.to));
  if (inPhase.length > 0) return inPhase;
  return recs.filter((r) => r.status === "recommended").slice(0, 1);
}

export function computeRecommendations(s: ConceptSnapshot): WorkflowRecommendation[] {
  const live = RULES.filter((r) => r.live(s));
  const primaryId = live
    .filter((r) => r.kind === "primary")
    .sort((a, b) => a.priority - b.priority)[0]?.id;

  const recs: WorkflowRecommendation[] = live.map((r) => {
    const status: RecStatus =
      r.kind === "gate" ? "blocked" : r.id === primaryId ? "recommended" : "available";
    return {
      id: r.id,
      status,
      priority: r.priority,
      ...r.build(s),
      walkthroughSection: RULE_SECTIONS[r.id],
    };
  });

  return recs.sort((a, b) => {
    if (a.status === "recommended" && b.status !== "recommended") return -1;
    if (b.status === "recommended" && a.status !== "recommended") return 1;
    return a.priority - b.priority;
  });
}
